import { useEffect, useMemo, useState } from 'react';
import { getOrganizationLogUsers } from '../../../services/SystemLoggingService';
import { LOG_ENTRY_TYPE_LABELS, LogEntryType } from '../../../types/SystemLogging.types';
import { buildUserFilterOptions } from '../../../utils/LogUserFilter.util';
import { AppliedFilters } from '../../ui/AppliedFilters/AppliedFilters';
import DatePicker from '../../ui/DatePicker/DatePicker';
import { DateRange } from '../../ui/DatePicker/DatePicker.type';
import { FilterDropdown } from '../../ui/FilterDropdown/FilterDropdown';
import { FilterOption } from '../../ui/FilterDropdown/FilterDropdown.types';

const baseTypeOptions: FilterOption[] = [
  { value: 'audit-ai-system', label: LOG_ENTRY_TYPE_LABELS['audit-ai-system'] },
  { value: 'audit-organization', label: LOG_ENTRY_TYPE_LABELS['audit-organization'] },
  { value: 'repo-sync', label: LOG_ENTRY_TYPE_LABELS['repo-sync'] },
];

// 'global' (platform-wide, cross-organization events) only ever returns
// results for a SUPER_ADMIN - the backend scopes an ADMIN to their own
// account regardless, so offering it to a non-super-admin would just be a
// filter option that silently always yields zero results.
const globalTypeOption: FilterOption = { value: 'global', label: LOG_ENTRY_TYPE_LABELS.global };

export type AuditLogsFilters = {
  types: LogEntryType[];
  userIds: string[];
  dateRange: DateRange | null;
};

type AuditLogsFiltersSectionProps = {
  filters: AuditLogsFilters;
  onFiltersChange: (filters: AuditLogsFilters) => void;
  isSuperAdmin?: boolean;
};

export function AuditLogsFiltersSection({
  filters,
  onFiltersChange,
  isSuperAdmin = false,
}: AuditLogsFiltersSectionProps) {
  const [userOptions, setUserOptions] = useState<FilterOption[]>([]);

  const typeOptions = useMemo(
    () => (isSuperAdmin ? [...baseTypeOptions, globalTypeOption] : baseTypeOptions),
    [isSuperAdmin]
  );

  useEffect(() => {
    let cancelled = false;

    getOrganizationLogUsers()
      .then((users) => {
        if (cancelled) return;
        setUserOptions(buildUserFilterOptions(users));
      })
      .catch(() => {
        if (!cancelled) setUserOptions([]);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const hasActiveFilters = useMemo(() => {
    return (
      filters.types.length > 0 ||
      filters.userIds.length > 0 ||
      filters.dateRange !== null
    );
  }, [filters]);

  const handleReset = () => {
    onFiltersChange({
      types: [],
      userIds: [],
      dateRange: null,
    });
  };

  return (
    <div className="flex items-center gap-2 w-full">
      <AppliedFilters />

      <div className="flex-1 min-w-0">
        <FilterDropdown
          placeholder="Type"
          options={typeOptions}
          selectedValues={filters.types}
          onSelectionChange={(values) =>
            onFiltersChange({ ...filters, types: values as LogEntryType[] })
          }
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        <FilterDropdown
          placeholder="User"
          options={userOptions}
          selectedValues={filters.userIds}
          onSelectionChange={(values) =>
            onFiltersChange({ ...filters, userIds: values })
          }
          className="w-full"
        />
      </div>

      <div className="flex-1 min-w-0">
        <DatePicker
          placeholder="Date"
          value={filters.dateRange}
          onDateRangeChange={(range) =>
            onFiltersChange({
              ...filters,
              dateRange: range.startDate && range.endDate ? range : null,
            })
          }
          className="w-full"
        />
      </div>

      <button
        className={`flex items-center gap-2 px-3 py-2 flex-shrink-0 transition-colors ${
          hasActiveFilters ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'
        }`}
        onClick={hasActiveFilters ? handleReset : undefined}
        disabled={!hasActiveFilters}
      >
        <span
          className={`text-xs leading-5 font-medium ${
            hasActiveFilters ? 'text-purple-800' : 'text-gray-200'
          }`}
        >
          Reset Filters
        </span>
      </button>
    </div>
  );
}
